import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import TacticalBoard from "@/components/TacticalBoard";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import {AttachmentAPI} from "@/api/attachment";
import "./index.scss";

export default function TacticalBoardPanel({
	entityType,
	entityId,
	attachments = [],
	onRefresh,
	selectedId,
	onSelect,
	onDelete,
	editable = true,
}) {
	const user = useSelector((state) => state?.user);
	const [board, setBoard] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	useEffect(() => {
		let active = true;
		setError("");
		setBoard(null);
		if (!selectedId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		AttachmentAPI.getTacticalBoard(selectedId)
			.then((data) => {
				if (active) setBoard(data);
			})
			.catch(() => {
				if (active)
					setError(
						"Impossibile aprire la lavagna. Riprova selezionandola dagli allegati.",
					);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [selectedId, entityType, entityId]);
	const saved = attachments.filter(
		(a) =>
			a.isTacticalBoard || /lavagna_tattica_.*\.png$/i.test(a.filePath || ""),
	);
	const storageKey = `${entityType}_${user?.clubId}_${user?.id || user?.username}_${entityId}_${selectedId || "new"}_${board?.revision || 0}`;
	const save = async (blob, drawing, asNew) => {
		try {
			const result = await AttachmentAPI.saveTacticalBoard(
				entityType,
				entityId,
				blob,
				drawing,
				!asNew && board ? board : null,
			);
			try {
				localStorage.removeItem(`tb_${storageKey}`);
			} catch {
				/* Salvataggio sul server concluso. */
			}
			const next = {
				id: result.id,
				revision: result.tacticalBoardRevision,
				drawing,
			};
			setBoard(next);
			onSelect(result.id);
			setError("");
			await onRefresh();
		} catch (err) {
			throw new Error(
				err.response?.data?.error || "Impossibile salvare la lavagna. Riprova.",
			);
		}
	};
	return (
		<div className="tactical-board-panel">
			<div className="tactical-board-panel__header">
				<div>
					<strong>
						{selectedId ? "Modifica lavagna salvata" : "Nuova lavagna"}
					</strong>
					<p>
						{selectedId
							? "Aggiorna lo stesso allegato oppure salva una nuova copia."
							: "Salva disegno e anteprima negli allegati per poterli riaprire in modifica."}
					</p>
				</div>
				{editable && selectedId && (
					<Button
						aria-label="Nuova lavagna"
						title="Nuova lavagna"
						onClick={() => onSelect(null)}
					>
						<AddIcon />
					</Button>
				)}
			</div>
			{error && <p role="alert">{error}</p>}
			{loading ? (
				<p>Caricamento lavagna...</p>
			) : (
				(!selectedId || board) && (
					<TacticalBoard
						key={`${entityId}_${selectedId || "new"}_${board?.revision || 0}`}
						storageKey={storageKey}
						initialDrawing={board?.drawing}
						editing={!!board}
						onSaveAttachment={editable ? save : undefined}
					/>
				)
			)}
			<section className="tactical-board-panel__saved">
				<h3>Lavagne salvate</h3>
				<AttachmentsContainer
					attachments={saved}
					editable={editable}
					handleEdit={onSelect}
					handleDelete={(id) => {
						onDelete(id);
					}}
				/>
			</section>
		</div>
	);
}
